import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Form,
  FormInstance,
  FormProps,
  Input,
  Row,
  Spin,
  Grid,
} from "antd";
import PhoneInput from "react-phone-input-2";
import "./style.scss";
import { ThreeDTransformIcon } from "../../components/svgs";
import { useStore } from "../../store";
import { observer } from "mobx-react-lite";
import FormField from "../../components/quote-request-form/form-field";
import { serviceNames, serviceTypes } from "../../stores/quotesStore";
import "react-phone-input-2/lib/style.css";
import { useNavigate, useParams } from "react-router-dom";

const { useBreakpoint } = Grid;

const QuoteRequestForm: React.FC = observer(() => {
  const { sm, xs } = useBreakpoint();
  const navigate = useNavigate();
  const { service } = useParams();
  const { quotesStore } = useStore();
  const { fields, getFormFields, sendQuoteRequest, isSending } = quotesStore;
  const formRef = useRef<FormInstance<any>>(null);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await getFormFields(service as string);
      setIsLoading(false);
    })();
    // eslint-disable-next-line
  }, [service]);

  const trackSubmitForm = () => {
    const sessionId = localStorage.getItem("sessionId");
    if (sessionId) {
      quotesStore.trackSubmitForm({ sessionId, service: service as string });
    }
  };

  const renderServiceCards = () => {
    return (
      <div className="my-8">
        <span className="font-semibold">
          Select the service you're interested in
        </span>
        <Row className="my-4" gutter={[16, 8]}>
          {serviceTypes.map((type, i) => (
            <Col md={4} sm={8} xs={12} className="mb-1" key={i}>
              <div className="w-full flex justify-center">
                <button
                  type="button"
                  className={`service-card ${
                    service === type ? "selected " : ""
                  }w-full rounded-3 border-danger`}
                  onClick={async () => {
                    await getFormFields(type);
                    navigate(`/quote/${type}`);
                  }}
                >
                  <ThreeDTransformIcon />
                  {serviceNames[i]}
                </button>
              </div>
            </Col>
          ))}
        </Row>
        <a href="https://wellstronics3d.com/services" className="font-danger font-l-bold">
          Learn About Services
        </a>
      </div>
    );
  };

  const handleFinish: FormProps<any>["onFinish"] = async (values) => {
    const sessionId = localStorage.getItem("sessionId");
    const formData = {
      ...values,
      service: service as string,
      sessionId: sessionId,
      created: new Date().toISOString()
    };
    const saved = await sendQuoteRequest(formData);
    // Keep the customer's input and skip the "submitted" metric if it did not save.
    if (!saved) return;
    trackSubmitForm();
    formRef.current?.resetFields();
  };

  const handleFinishFailed: FormProps<any>["onFinishFailed"] = (errorInfo) => {
    alert("Please fill in the form.");
    console.log("Failed:", errorInfo);
  };

  return (
    <div className="w-full flex-col items-center">
      <span className={`mt-10 form-title ${sm || xs ? "text-center" : ""}`}>
        Request a Quote
      </span>
      <span className="mt-4 mb-10">
        Fill out this form and we'll get back to you with a quote right away.
      </span>
      <Form
        className="max-w-270 w-full bg-primary border-primary rounded-4 p-8"
        onFinish={handleFinish}
        onFinishFailed={handleFinishFailed}
        ref={formRef}
      >
        {renderServiceCards()}
        <Row gutter={16}>
          <Col md={8} sm={12} xs={24} className="mb-1">
            <span className="font-semibold">Name*</span>
            <Form.Item
              name="name"
              rules={[{ required: true, message: "Please input your name" }]}
            >
              <Input placeholder="Full Name" className="mt-3" />
            </Form.Item>
          </Col>
          <Col md={8} sm={12} xs={24} className="mb-1">
            <span className="font-semibold">Email*</span>
            <Form.Item
              name="email"
              rules={[{ required: true, message: "Please input your email" }]}
            >
              <Input
                type="email"
                placeholder="bob@company.com"
                className="mt-3"
              />
            </Form.Item>
          </Col>
          <Col md={8} sm={12} xs={24} className="mb-1">
            <span className="font-semibold">Phone*</span>
            <Form.Item
              name="phone"
              rules={[
                () => ({
                  validator(_, value) {
                    if (value.length < 15) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Your phone number is not valid!")
                    );
                  },
                }),
              ]}
            >
              <div className="mt-3">
                <PhoneInput
                  disableDropdown
                  country="us"
                  onlyCountries={["us"]}
                  placeholder="(123) 456-7890"
                  disableCountryCode
                  inputStyle={{ width: "100%" }}
                  defaultErrorMessage={"Please input your phone number"}
                />
              </div>
            </Form.Item>
          </Col>
        </Row>
        {isLoading ? (
          <div className="flex justify-center">
            <Spin spinning={isLoading} className="my-10" />
          </div>
        ) : (
          <>
            <FormField fields={fields} />
            {service !== null && (
              <Form.Item>
                <Button
                  htmlType="submit"
                  className="w-full h-10 rounded-8 font-large bg-danger"
                  disabled={isSending}
                >
                  {isSending ? "Submitting..." : "Submit Form"}
                </Button>
              </Form.Item>
            )}
          </>
        )}
      </Form>
    </div>
  );
});

export default QuoteRequestForm;
